// Resolves where the app should land after a customer/job switch (#962 Obs 1).
//
// Before this, `navigateToNewJob` in contexts/account.tsx hardcoded the post-switch
// route to 'tables' for almost every case, so switching job while in DataViz (or
// Files, AutoCoder, etc.) silently bounced the user back to Tables. This module
// maps the *current* top-level route to that area's home/list route, so the
// switch preserves whichever area the user is already in.
//
// A job-scoped entity open in the old job (a specific dashboard, pipeline, code
// frame, etc.) cannot survive the switch — its id won't resolve against the new
// job — so every area maps to its list/landing route, never to a detail route.
//
// Two TikTok rules sit above area preservation (Track 6 / T6.2):
// - Switching TO a TikTok job always lands on the Sky product CHOOSER
//   ('sky-social-media') — the chooser shows only the products the user may
//   open, so an unentitled user sees an empty chooser rather than a dead
//   route. (Admin is the one area that survives a TikTok switch.)
// - The social suite's pages only make sense for TikTok jobs, so leaving one
//   for a non-TikTok job falls back to Tables.

export interface JobSwitchDestination {
  /** route name to navigate to */
  routeName: string
  /** whether the destination route takes a :job param */
  needsJob: boolean
}

const TIKTOK_ONLY_AREAS = new Set([
  'tiktok-comments',
  'tiktok-analytics',
  'tiktok-start',
  'tiktok-dashboard',
  'sky-social-media',
  'sky-social-media-product',
  'sky-social-media-product-spend',
  // The post-data editor (#1196). It is pinned to bprsky/tiktok in code — the
  // endpoints it reads are on that job's AutoCoder mount — so it belongs in the
  // TikTok-only set exactly as the dashboards do: switching to another TikTok
  // job keeps you in the social suite, and switching away falls back to Tables
  // rather than leaving you on a page whose customer no longer matches.
  'sky-social-media-entities',
  // The reconciliation page (16 Oct 2026), pinned to bprsky/tiktok the same way.
  'sky-social-media-reconciliation',
  // The influencer upload (#1694), beside the post-data editor and pinned the same way.
  'sky-social-media-influencer-data',
])

const TABLES_FALLBACK: JobSwitchDestination = { routeName: 'tables', needsJob: true }

// The chooser route has no :customer/:job params; the job still rides along as
// a query param, matching how navigateToNewJob has always called it (T6.2).
const SKY_SOCIAL_CHOOSER: JobSwitchDestination = { routeName: 'sky-social-media', needsJob: true }

// Maps a current route name to its area's home/list route. Returns undefined
// for routes with no generalizable per-job area (e.g. the programme tracker,
// which is keyed by a tracker slug, not customer/job).
//
// This is a hand-maintained registry, separate from routes.tsx (#1066 item
// 4): a new or renamed route silently falls through to the Tables fallback
// below unless someone remembers to add it here — the exact #962 bug this
// module fixes, one area at a time. `jobSwitchNavigation.routeCoverage.test.ts`
// reads every route name out of routes.tsx and fails if one is neither
// mapped here nor in that test's explicit no-generalizable-area allowlist,
// so drift can't land silently.
export function areaHomeForRoute(routeName: string): JobSwitchDestination | undefined {
  if (routeName === 'admin' || routeName.startsWith('admin-')) {
    return { routeName: 'admin', needsJob: false }
  }
  if (routeName === 'dashboards' || routeName.startsWith('dashboard-')) {
    // DataViz list is job-scoped off account.job, not a route param (#962).
    return { routeName: 'dashboards', needsJob: false }
  }
  if (routeName === 'dashboard') {
    return { routeName: 'dashboard', needsJob: true }
  }
  if (['tables', 'multitables', 'editspec', 'batch-generate', 'batch-regenerate'].includes(routeName)) {
    return TABLES_FALLBACK
  }
  if (routeName === 'variables' || routeName === 'variables-data' || routeName === 'variables-tools') {
    return { routeName, needsJob: true }
  }
  if (routeName === 'files') {
    return { routeName: 'files', needsJob: true }
  }
  if (routeName === 'executive') {
    return { routeName: 'executive', needsJob: true }
  }
  if (routeName === 'autocoder' || routeName.startsWith('autocoder-') || routeName === 'batch-import') {
    return { routeName: 'autocoder', needsJob: true }
  }
  if (routeName === 'themeai' || routeName.startsWith('theme-ai')) {
    return { routeName: 'theme-ai-tasks', needsJob: true }
  }
  if (routeName === 'frameworks' || routeName === 'framework-editor' || routeName === 'framework-suggestions') {
    return { routeName: 'frameworks', needsJob: true }
  }
  if (routeName === 'visualizations') {
    return { routeName: 'visualizations', needsJob: true }
  }
  if (routeName === 'visualizations-real') {
    return { routeName: 'visualizations-real', needsJob: true }
  }
  if (TIKTOK_ONLY_AREAS.has(routeName)) {
    return SKY_SOCIAL_CHOOSER
  }
  if (routeName === 'agent' || routeName.startsWith('agent-')) {
    return { routeName: 'agent', needsJob: false }
  }
  if (routeName === 'alerts' || routeName.startsWith('alert-')) {
    return { routeName: 'alerts', needsJob: false }
  }
  if (routeName === 'my-subscriptions') {
    return { routeName: 'my-subscriptions', needsJob: false }
  }
  if (routeName === 'ai-access') {
    return { routeName: 'ai-access', needsJob: false }
  }
  // A guided conversation is keyed by its response, not a job: switching
  // customer lands on that customer's list of invitations.
  if (routeName === 'guided' || routeName.startsWith('guided-')) {
    return { routeName: 'guided', needsJob: false }
  }
  if (routeName === 'sav-import') {
    return { routeName: 'sav-import', needsJob: false }
  }
  return undefined
}

function isTikTokJobName(jobName: string): boolean {
  const name = (jobName || '').toLowerCase()
  return name.includes('tiktok') || name.includes('tik-tok') || name.includes('tik_tok')
}

/**
 * Resolves the route to land on after switching customer/job: the current
 * top-level area's home route, or Tables when that area doesn't exist for
 * the new job (or the current route maps to no generalizable area at all).
 * TikTok jobs override area preservation and land on the product chooser
 * (T6.2), except from Admin.
 */
export function resolveJobSwitchDestination(
  currentRouteName: string | undefined,
  newJobName: string,
): JobSwitchDestination {
  const home = currentRouteName ? areaHomeForRoute(currentRouteName) : undefined

  if (isTikTokJobName(newJobName)) {
    return home?.routeName === 'admin' ? home : SKY_SOCIAL_CHOOSER
  }

  if (!home) return TABLES_FALLBACK

  if (currentRouteName && TIKTOK_ONLY_AREAS.has(currentRouteName)) {
    return TABLES_FALLBACK
  }

  return home
}
